import { BodyScrollEvent, ContextMenuVisibleChangedEvent } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { useCallback, useRef } from 'react';

/**
 * AG Grid v35 anchors the context menu to the grid body (not the clicked cell), so an internal
 * scroll leaves the menu floating over rows it no longer refers to. Close it on body scroll
 * instead, like a native context menu. `hidePopupMenu()` would also close an open column/filter
 * menu — those anchor to the header and survive body scroll by design — so the hide only fires
 * while a context menu reports itself visible.
 *
 * Like useGridTrackingHandlers, the returned handlers must be passed as top-level
 * `<AgGridReact>` props so they win over any `gridOptions` counterpart.
 */
export function useCloseContextMenuOnBodyScroll(props: AgGridReactProps) {
  const contextMenuVisibleRef = useRef(false);

  const callerOnContextMenuVisibleChanged =
    props.onContextMenuVisibleChanged ?? props.gridOptions?.onContextMenuVisibleChanged;
  const onContextMenuVisibleChanged = useCallback(
    (event: ContextMenuVisibleChangedEvent) => {
      contextMenuVisibleRef.current = event.visible;
      callerOnContextMenuVisibleChanged?.(event);
    },
    [callerOnContextMenuVisibleChanged]
  );

  const callerOnBodyScroll = props.onBodyScroll ?? props.gridOptions?.onBodyScroll;
  const onBodyScroll = useCallback(
    (event: BodyScrollEvent) => {
      if (contextMenuVisibleRef.current) {
        event.api.hidePopupMenu();
      }
      callerOnBodyScroll?.(event);
    },
    [callerOnBodyScroll]
  );

  return { onBodyScroll, onContextMenuVisibleChanged };
}
